import classNames from "classnames";
import React, {
    AnchorHTMLAttributes,
    ReactElement, ReactNode, useContext, useEffect, useMemo,
    useState,
} from "react";
import {
    Navigate as BaseNavigate,
    Link as RouterLink, NavLink as RouterNavLink,
    useMatch as useBaseRouteMatch,
    useNavigate,
    useLocation as useRouteLocation,
} from "react-router-dom";
import { SizeTypes, VariantTypes } from "../components/theme/button/Button";

export const Paths = {

    ACCOUNT_PROFLE: "/profile",

    ANSWERS_NEW: "/answers/new",
    ANSWERS_GET: "/answers/:id",
    ANSWERS_GET_VERSION: "/answers/:id/:versionId",
    ANSWERS_LIST: "/answers",

    LOGIN: "/login",
    LOGIN_WITH_TOKEN: "/login/token/:token",
    LOGIN_RESET: "/login/password-reset",

    CHANNEL_INVITE: "/channel-invite/:id",

    AGENT: "/agent",
    AGENT_COMPOSE: "/agent/new",
    AGENT_CHAT: "/agent/:chatId",

    TICKETS: "/tickets",
    TICKETS_CREATE: "/tickets/new",
    TICKETS_VIEW: "/tickets/:ticketId",

    CONTACTS: "/contacts",
    CONTACTS_ENTITY: "/contacts/:entity",
    ORGANIZATIONS_ID: "/contacts/organizations/:id",
    ORGANIZATIONS_NEW: "/contacts/organizations/new",
    ORGANIZATION_USERS: "/contacts/organizations/:companyId/users",
    USERS_ID: "/contacts/users/:id",
    USERS_NEW: "/contacts/users/new",

    REPORTS: "/reports",
    REPORTS_GET: "/reports/:reportId",

    DEFAULT: "/",
    ADMINISTRATOR: "/admin",
    ADMINISTRATOR_SETTING: "/admin/:setting",
    ADMINISTRATOR_SETTING_ID: "/admin/:setting/:id",
    ADMINISTRATOR_SETTING_ID_CHILD: "/admin/:setting/:id/:childId",

    SITE_ADMINISTRATOR: "/site-admin",
    SITE_ADMINISTRATOR_SETTING: "/site-admin/:setting",
    SITE_ADMINISTRATOR_SETTING_ID: "/site-admin/:setting/:id",

    ONBOARDING: "/onboarding",
    ONBOARDING_STEP: "/onboarding/:stepId",
    WAIT_FOR_DOMAIN: "/onboarding/wait-for-domain",
    ONBOARDING_TEAMS_INSTALLED: "/onboarding/microsoft-teams/installed/:token",
    ONBOARDING_TEAMS_INSTALLED_CHANNEL: "/onboarding/microsoft-teams/installed-channel/:token",

    HOME: "/home",

    ACTION: "/actions/:token/:actionType/*",
    ACTION_FEEDBACK: "/actions/:token/feedback/:type/:score",
    ACTION_DEFLECTION: "/actions/:token/deflection/:type/",

    ERROR_404: "/errors/404",
    ERROR_INTERNAL: "/errors/internal",
} as const;

export interface Params extends Record<PathValues, Record<string, string>> {
    [Paths.ANSWERS_NEW]: {
        tagId: string
    },
    [Paths.AGENT]: {
        search?: string,
        archived?: string,
        sortBy?: string,
        groupBy?: string,
        filters?: string
    },
    [Paths.AGENT_CHAT]: {
        search: string
    },
    [Paths.ADMINISTRATOR_SETTING_ID]: {
        error: string,
        isSuccess: string,
        feature: string;
    },
    [Paths.DEFAULT]: {
        redirect: string,
        isWaitLogin: string
    },
    [Paths.LOGIN]: {
        error?: string,
        redirect: string;
    },
    [Paths.LOGIN_WITH_TOKEN]: {
        redirect: string;
    },
    [Paths.TICKETS]: {
        requestTypeId: string
    },
    [Paths.USERS_NEW]: {
        companyId: string
    },
    [Paths.ONBOARDING_STEP]: {
        error: string,
        isSuccess: string,
        serviceId: string,
    },
    [Paths.HOME]: {
        isSuccess: string,
        isOnboarded: string
    },
    [Paths.WAIT_FOR_DOMAIN]: {
        domain: string,
        redirect: string,
    },
    [Paths.ONBOARDING_TEAMS_INSTALLED]: {
        isAssociated: string,
        organization: string
    },
    [Paths.ANSWERS_LIST]: {
        search: string
    },
}

export type ExtractParams<T> =
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    T extends `${infer _Start}:${infer Param}/${infer Rest}` ? { [k in Param | keyof ExtractParams<Rest>]: string } :
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // eslint-disable-next-line @typescript-eslint/indent
    T extends `${infer _Start}:${infer Param}` ? { [k in Param]: string } : never;

export type DynamicParameter<T> = [T] extends [never] ? [] : [T];

export type PathValues = (typeof Paths)[keyof typeof Paths];

export const buildUrl = <P extends PathValues>(
    path: P,
    ...params: DynamicParameter<ExtractParams<P>>
): string => {
    let ret: string = path;

    if (params.isNotEmpty) {
        const paramObj: Record<string, string> = params.firstElement;
        if (paramObj) {
            Object.entries(paramObj).forEach(([key, value]) => {
                ret = ret.replace(`:${key}`, encodeURIComponent(value));
            });
        }
    }

    return ret;
};

const EMPTY = Object.create(null);

export enum PathMatchType {
    FULL_PATH,
    PARTIAL_PATH
}

export const useParams = <P extends PathValues>(
    path: P, matchType = PathMatchType.FULL_PATH,
): ExtractParams<P> => {
    // `exact`, `sensitive` and `strict` options are set to true
    // to ensure type safety.
    const match = useBaseRouteMatch({
        path,
        caseSensitive: true,
        end: matchType === PathMatchType.FULL_PATH,
    });

    if (!match) {
        return EMPTY;
    }
    return match.params as ExtractParams<P> ?? {};
};

export const useParamsAndPath = <P extends PathValues>(
    path: P, matchType = PathMatchType.FULL_PATH,
): {path: P, params: ExtractParams<P>} => {
    // `exact`, `sensitive` and `strict` options are set to true
    // to ensure type safety.
    const match = useBaseRouteMatch({
        path,
        caseSensitive: true,
        end: matchType === PathMatchType.FULL_PATH,
    });

    if (!match) {
        return EMPTY;
    }
    return {
        params: match.params as ExtractParams<P> ?? {},
        path: match.pathname as P,
    };
};

export const isCurrentRoute = <P extends PathValues>(
    path: P,
): boolean => {
    // `exact`, `sensitive` and `strict` options are set to true
    // to ensure type safety.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const match = useBaseRouteMatch({
        path,
        caseSensitive: true,
    });
    return match != null;
};

export const isRouteMatch = <P extends PathValues>({
    path,
}: {
    path: P;
    exact?: boolean;
    strict?: boolean;
    sensitive?: boolean;
},

): boolean => {
    // `exact`, `sensitive` and `strict` options are set to true
    // to ensure type safety.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const match = useBaseRouteMatch({
        path,
        caseSensitive: true,
    });

    return match != null;
};

const Routing = {
    Paths,
    buildUrl,
};

export interface RoutingProps {
    Paths: typeof Paths;
    buildUrl: typeof buildUrl;
    historyPush: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => void;
    historyPushWithSearch: <P extends PathValues>(
        path: P, searchParams: Params[P], ...params: DynamicParameter<ExtractParams<P>>
    ) => void;
    historyPushSearch: <P extends PathValues>(searchParams: Params[P]) => void;
    createHistoryPush: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => (() => void);
    redirect: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => void;
    getSearchParams: <P extends PathValues>(path?: P) => P extends undefined ? Record<string, string | string[]> : Params[P];
}

export const RoutingContext = React.createContext<RoutingProps>({
    Paths,
    buildUrl,
    historyPush: () => { /* default */ },
    historyPushWithSearch: () => { /* default */ },
    historyPushSearch: () => { /* default */ },
    createHistoryPush: () => () => { /* default */ },
    redirect: () => { /* default */ },
    getSearchParams: () => Object.create(null),
});

function parseParams(searchParams: Record<string, string>): URLSearchParams {
    const params = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach(child => params.append(key, child));
        } else {
            params.append(key, value);
        }
    });
    return params;
}

export function getSearchParamsWithObjects() {
    const obj = Object.create(null);

    let isDirty = false;
    new URLSearchParams(window.location.search).forEach((value, fullKey) => {
        let parent = obj;
        const parts = fullKey.split(".");
        parts.limit(-1).forEach(part => {
            if (parent[part] == null) {
                parent[part] = Object.create(null);
            }
            parent = parent[part];
        });
        const key = parts.lastElement;

        let curr = parent[key];
        if (curr == null) {
            if (value != null && value !== "") {
                isDirty = true;
                parent[key] = value;
            }
            return;
        }
        if (!Array.isArray(curr)) {
            curr = [curr];
            isDirty = true;
            parent[key] = curr;
        }
        if (Array.isArray(curr)) {
            curr.push(value);
        }
    });
    return isDirty ? obj : {};
}
export function RoutingProvider({ children }: { children: ReactElement }): (ReactElement | null) {
    const navigate = useNavigate();

    const current = useMemo(() => ({
        ...Routing,
        historyPush: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => {
            const url = buildUrl(path, ...params);
            navigate(url);
        },
        historyPushWithSearch: <P extends PathValues>(
            path: P, searchParams: Record<string, string>, ...params: DynamicParameter<ExtractParams<P>>
        ) => {
            const url = buildUrl(path, ...params);
            const search = parseParams(searchParams).toString();
            navigate({
                pathname: url,
                search,
                // search: parseParams(searchParams).toString(),
            });
        },
        historyPushSearch: (searchParams: Record<string, string>) => {
            navigate({
                pathname: window.location.pathname,
                search: parseParams(searchParams).toString(),
            });
        },
        createHistoryPush: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => {
            const url = buildUrl(path, ...params);
            return () => {
                navigate(url);
            };
        },
        redirect: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => {
            const url = buildUrl(path, ...params);
            window.location.href = url;
        },

        getSearchParams() {
            const obj = Object.create(null);

            let isDirty = false;
            new URLSearchParams(window.location.search).forEach((value, key) => {
                let curr = obj[key];
                if (curr == null) {
                    if (value != null && value !== "") {
                        isDirty = true;
                        obj[key] = value;
                    }
                    return;
                }
                if (!Array.isArray(curr)) {
                    curr = [curr];
                    isDirty = true;
                    obj[key] = curr;
                }
                if (Array.isArray(curr)) {
                    curr.push(value);
                }
            });
            return isDirty ? obj : {};
        },
    }), []);
    return (
        <RoutingContext.Provider value={current}>
            {children}
        </RoutingContext.Provider>
    );
}

export default function useRouting(): RoutingProps {
    return useContext(RoutingContext) ?? {};
}

export interface TypedLinkProps<
    P extends PathValues, SearchParams extends Params[P] = Params[P]
> extends AnchorHTMLAttributes<HTMLAnchorElement> {
    to: P,
    className?: string;
    replace?: boolean;
    children?: ReactNode;
    search?: SearchParams extends never ? Record<string, string> : Partial<SearchParams>;
}

export type ConditionalParams<P extends PathValues> =
    // eslint-disable-next-line @typescript-eslint/ban-types
    TypedLinkProps<P> & (ExtractParams<P> extends never ? {} : { params: ExtractParams<P> });

export interface TypedLinkPropsWithParams<P extends PathValues> extends TypedLinkProps<P> {
    // eslint-disable-next-line @typescript-eslint/ban-types
    params?: ExtractParams<P>
}

function hasValues(obj: Record<string, unknown>): boolean {
    return Object.values(obj).filter(row => row != null && row !== "").isNotEmpty;
}

export type LinkButtonProps<T extends PathValues> = ConditionalParams<T>;

const LinkButton = React.forwardRef(<P extends PathValues>(
    args: LinkButtonProps<P>,
    ref: React.ForwardedRef<HTMLAnchorElement>,
): ReactElement => {
    const {
        search,
        to,
        children: elementChildren,
        params,
        ...rest
    } = args as (typeof args & { params: ExtractParams<P> });

    // export function NavLink<P extends PathValues>(params: ConditionalParams<P>): ReactElement {
    const children = [params];
    return (
        <RouterLink
            {...rest}
            ref={ref}
            to={{
                pathname: buildUrl(to, ...children as DynamicParameter<ExtractParams<P>>),
                search: search && hasValues(search)
                    ? new URLSearchParams(search as Record<string, string>).toString()
                    : undefined,
            }}
        >
            {elementChildren}
        </RouterLink>
    );
});

const Link = LinkButton as <
    T extends PathValues,
> (
    props: LinkButtonProps<T> & { ref?: React.ForwardedRef<HTMLAnchorElement> }
) => ReturnType<typeof LinkButton>;

export { Link };

export type NavLinkButtonParams<P extends PathValues> = ConditionalParams<P> & {
    size?: SizeTypes;
    variant?: VariantTypes;
    isActive?: boolean;
    isButton?: boolean
};

const NavLinkButton = React.forwardRef(<P extends PathValues>(
    args: NavLinkButtonParams<P>,
    ref: React.ForwardedRef<HTMLAnchorElement>,
): ReactElement => {
    const {
        size,
        variant,
        className,
        isActive,
        isButton,
        to,
        replace,
        params,
        children: elementChildren,
        search,
        ...rest
    } = args as (typeof args & { params: ExtractParams<P>[] });

    const children = [params];
    return (
        <RouterNavLink
            {...rest}
            to={{
                pathname: buildUrl(to, ...children as DynamicParameter<ExtractParams<P>>),
                search: search && hasValues(search)
                    ? new URLSearchParams(search as Record<string, string>).toString()
                    : undefined,
            }}
            replace={replace}
            className={classNames(
                isButton ? "btn" : "",
                variant ? `btn-${variant}` : "",
                size ? `btn-${size}` : "",
                isActive ? "active" : "",
                className,
            )}
            ref={ref}
        >
            {elementChildren}
        </RouterNavLink>
    );
});
NavLinkButton.defaultProps = {
    isButton: false,
};

const NavLink = NavLinkButton as <
    T extends PathValues,
> (
    props: NavLinkButtonParams<T> & { ref?: React.ForwardedRef<HTMLAnchorElement> }
) => ReturnType<typeof NavLinkButton>;

export { NavLink };

export function Navigate<P extends PathValues>(params: ConditionalParams<P>): ReactElement {
    const children = [(params as unknown as ({ params: ExtractParams<P> }))?.params];
    return (
        <BaseNavigate to={{
            pathname: buildUrl(params.to, ...children as DynamicParameter<ExtractParams<P>>),
            search: params.search && hasValues(params.search)
                ? new URLSearchParams(params.search as Record<string, string>).toString()
                : undefined,
        }}
        />
    );
}

export function filterEmptyParameters<T>(input: Record<string, unknown>): T {
    const params = Object.create(null);
    if (input) {
        Object.entries(input).forEach(([key, value]) => {
            if (value != null && value !== "" && (!Array.isArray(value) || value.isNotEmpty)) {
                params[key] = value;
            }
        });
    }

    return params as T;
}

export function useParameterFilters<T extends object>(
    path: PathValues,
    defaultValue?: (T | (() => T)),
): [T, ((key: keyof T, value: T[keyof T]) => void), ((value: T) => void)] {
    const { historyPushSearch, getSearchParams } = useContext(RoutingContext) ?? {};

    const [filters, setFilters] = useState<T>(() => {
        let params = getSearchParams(path);
        if (!params || Object.keys(params).isEmpty) {
            params = (typeof defaultValue === "function" ? defaultValue() : defaultValue) as T;
        }
        return filterEmptyParameters(params);
    });

    useEffect(() => {
        const params = Object.create(null);
        Object.entries({ ...filters }).forEach(([key, value]) => {
            if (value != null && value !== "" && (!Array.isArray(value) || value.isNotEmpty)) {
                params[key] = value instanceof Date ? value.toISOString() : value;
            }
        });

        historyPushSearch(params);
    }, [filters]);

    const setValue = (key: keyof T, value: T[keyof T]) => {
        setFilters({
            ...filters,
            [key]: value,
        });
    };

    return [filters, setValue, setFilters];
}
